import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AddFormComponent } from '@shared/components/add-form/add-form.component';
import type { FormField } from '@shared/components/add-form/add-form.component';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

import {
  OfferCreateRequest,
  OfferResponse,
  OfferUpdateRequest,
} from '@app/api/promotion-service';
import { PriceGroupControllerService } from '@app/api/promotion-service/api/priceGroupController.service';
import { ValidationPeriodControllerService } from '@app/api/promotion-service/api/validationPeriodController.service';
import type { PriceGroup } from '@app/api/promotion-service/model/priceGroup';
import type { ValidationPeriodListResponse } from '@app/api/promotion-service/model/validationPeriodListResponse';
import type { OfferSchemaApiRoot } from '@app/api/promotion-service/model/offerSchemaApiRoot';
import type { OfferSchemaApiField } from '@app/api/promotion-service/model/offerSchemaApiField';

import { OfferService } from '../../services/offer.service';
import {
  OFFER_BASE_FORM_FIELDS,
  OFFER_HEADER_ACTIONS,
} from './offer-upsert.config';
import { OfferSchemaControllerService } from '@app/api/promotion-service/api/offerSchemaController.service';
import { withPaginatedFormFields } from '@shared/utils/paginated-field.helpers';
import { mapPageContentToPaginatedPage } from '@shared/utils/paginated-dropdown.util';
import {
  dateFromApiDate,
  dateFromLocalTime,
  formatApiDate,
  formatApiLocalTime,
} from './offer-date-time.util';
import {
  DEF_FIELD_PREFIX,
  definitionControlName,
  emptyRowFromProperties,
  flattenDefinitionForForm,
  getNested,
  inferredBoundsOf,
  jsonTypeOf,
  listSchemaArrayFields,
  mapOfferSchemaFieldToFormField,
  optionLabelFor,
  optionLabelViFor,
  rowsFromApiArray,
  SchemaArrayFieldDef,
  SchemaArrayRow,
  serializeRowsForApi,
  setNested,
} from './offer-schema-form.util';

/** Declarative mutual-exclusion condition served by the backend schema (field.disabledWhen). */
interface OfferFieldCondition {
  field: string;
  op: 'FILLED' | 'NON_EMPTY' | 'EQUALS' | 'NOT_EQUALS';
  value?: string;
  label?: string;
  labelVi?: string;
}

@Component({
  selector: 'app-offer-upsert',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AddFormComponent,
    ToastModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
  ],
  templateUrl: './offer-upsert.component.html',
  styleUrl: './offer-upsert.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class OfferUpsertComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly offerService = inject(OfferService);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly offerSchemaApi = inject(OfferSchemaControllerService);
  private readonly priceGroupController = inject(PriceGroupControllerService);
  private readonly validationPeriodController = inject(
    ValidationPeriodControllerService,
  );

  private readonly destroy$ = new Subject<void>();
  @ViewChild('addForm') private readonly addFormComponent?: AddFormComponent;

  loading = signal<boolean>(false);
  initialData = signal<Record<string, unknown>>({});
  formFields = signal<FormField[]>(this.cloneBaseFields());
  headerActions = OFFER_HEADER_ACTIONS;
  currentOfferType = signal<string>('');
  schemaRoot = signal<OfferSchemaApiRoot | null>(null);
  schemaArrayData = signal<Record<string, SchemaArrayRow[]>>({});

  private offerId: number | null = null;
  private skipCanDeactivate = false;
  private schemaArraysDirty = false;
  private isHydratingForm = false;

  readonly schemaArrayFieldDefs = computed(() =>
    listSchemaArrayFields(this.schemaRoot()?.fields),
  );
  readonly showConfiguration = computed(() => Boolean(this.currentOfferType()));

  rowsForField(fieldName: string): SchemaArrayRow[] {
    return this.schemaArrayData()[fieldName] ?? [];
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.offerId = idParam ? Number(idParam) : null;

    if (this.offerId) {
      this.loadOffer(this.offerId);
    } else {
      this.setDefaultOfferFormValues();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get editMode(): boolean {
    return this.offerId !== null;
  }

  arraySectionTitle(def: SchemaArrayFieldDef): string {
    const vi = this.translateService.currentLang?.startsWith('vi');
    return vi && def.labelVi ? def.labelVi : def.label;
  }

  propHeaderLabel(p: OfferSchemaApiField): string {
    const vi = this.translateService.currentLang?.startsWith('vi');
    const ext = p as OfferSchemaApiField & { labelVi?: string };
    return vi && ext.labelVi ? ext.labelVi : (p.label ?? p.name ?? '');
  }

  isRequiredProp(p: OfferSchemaApiField): boolean {
    return !!(p as OfferSchemaApiField & { required?: boolean }).required;
  }

  propCellKind(
    p: OfferSchemaApiField,
  ): 'dropdown' | 'number' | 'text' {
    const j = jsonTypeOf(p);
    if (j === 'ENUM' || (p.enumValues?.length ?? 0) > 0) {
      return 'dropdown';
    }
    if (j === 'BOOLEAN' || j === 'BOOL') {
      return 'dropdown';
    }
    if (
      j === 'NUMBER' ||
      j === 'INTEGER' ||
      j === 'INT' ||
      j === 'LONG' ||
      j === 'DOUBLE' ||
      j === 'FLOAT' ||
      j === 'BIGDECIMAL'
    ) {
      return 'number';
    }
    return 'text';
  }

  cellNumberMin(p: OfferSchemaApiField): number {
    return inferredBoundsOf(p.name ?? '').min ?? -Infinity;
  }

  cellNumberMax(p: OfferSchemaApiField): number {
    return inferredBoundsOf(p.name ?? '').max ?? Infinity;
  }

  cellDropdownOptions(p: OfferSchemaApiField): {
    label: string;
    value: string | boolean;
  }[] {
    const jt = jsonTypeOf(p);
    if (jt === 'BOOLEAN' || jt === 'BOOL') {
      const vi = this.translateService.currentLang?.startsWith('vi');
      return [
        { label: vi ? 'Có' : 'Yes', value: true },
        { label: vi ? 'Không' : 'No', value: false },
      ];
    }
    const vi = this.translateService.currentLang?.startsWith('vi');
    return (p.enumValues ?? []).map((ev) => ({
      label: vi
        ? (optionLabelViFor(p, ev) ?? optionLabelFor(p, ev))
        : optionLabelFor(p, ev),
      value: ev,
    }));
  }

  onFormAction(action: string, formData: Record<string, unknown>): void {
    if (action === 'save') {
      this.saveOffer(formData);
      return;
    }
    if (action === 'cancel') {
      window.history.back();
    }
  }

  onFormValueChanges(formData: Record<string, unknown>): void {
    if (this.isHydratingForm) {
      return;
    }
    const kind = this.normalizeOfferKind(
      formData['offerType'] ?? this.currentOfferType(),
      '',
    );
    if (kind === this.currentOfferType()) {
      this.applyExclusionEffects(formData);
      return;
    }
    this.preserveBaseFieldsBeforeSchemaReload(formData);
    this.currentOfferType.set(kind);
    this.schemaRoot.set(null);
    this.schemaArrayData.set({});
    this.stripDefinitionKeysFromInitialData();
    this.applySchemaToFormFields();
    if (kind) {
      this.loadOfferSchema(kind);
    }
  }

  private stripDefinitionKeysFromInitialData(): void {
    const cur = { ...this.initialData() };
    for (const k of Object.keys(cur)) {
      if (k.startsWith(DEF_FIELD_PREFIX)) {
        delete cur[k];
      }
    }
    this.initialData.set(cur);
  }

  /** Only when offer kind changes — avoids resetting configuration fields on every keystroke. */
  private preserveBaseFieldsBeforeSchemaReload(
    formData: Record<string, unknown>,
  ): void {
    const next = { ...this.initialData() };
    for (const field of OFFER_BASE_FORM_FIELDS) {
      const key = field.controlName;
      if (Object.prototype.hasOwnProperty.call(formData, key)) {
        next[key] = formData[key];
      }
    }
    this.initialData.set(next);
  }

  addSchemaArrayRow(fieldName: string, props: OfferSchemaApiField[]): void {
    const data = { ...this.schemaArrayData() };
    const rows = [...(data[fieldName] ?? []), emptyRowFromProperties(props)];
    data[fieldName] = rows;
    this.schemaArrayData.set(data);
    this.schemaArraysDirty = true;
    this.recomputeExclusion();
  }

  removeSchemaArrayRow(fieldName: string, rowId: string): void {
    const data = { ...this.schemaArrayData() };
    data[fieldName] = (data[fieldName] ?? []).filter((r) => r.id !== rowId);
    this.schemaArrayData.set(data);
    this.schemaArraysDirty = true;
    this.recomputeExclusion();
  }

  patchSchemaArrayCell(
    fieldName: string,
    rowId: string,
    prop: string,
    value: unknown,
  ): void {
    const data = { ...this.schemaArrayData() };
    const rows = [...(data[fieldName] ?? [])];
    const idx = rows.findIndex((r) => r['id'] === rowId);
    if (idx < 0) {
      return;
    }
    rows[idx] = { ...rows[idx], [prop]: value };
    data[fieldName] = rows;
    this.schemaArrayData.set(data);
    this.schemaArraysDirty = true;
  }

  private loadOffer(id: number): void {
    this.isHydratingForm = true;
    this.loading.set(true);
    this.offerService
      .getOfferById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (offer) => {
          const kind = this.normalizeOfferKind(
            offer.kind ?? 'PERIODIC_DISCOUNT',
          );
          this.currentOfferType.set(kind);
          this.loadOfferSchema(kind, offer);
        },
        error: () => {
          this.isHydratingForm = false;
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: 'Failed to load offer',
          });
        },
      });
  }

  private loadOfferSchema(kind: string, offer?: OfferResponse): void {
    const normalizedKind = this.normalizeOfferKind(kind);
    this.offerSchemaApi
      .byKind(normalizedKind, undefined, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (root: OfferSchemaApiRoot) => {
          this.applySchemaResponse(root, offer);
        },
        error: () => {
          this.applySchemaResponse(null, offer);
        },
      });
  }

  private applySchemaResponse(
    root: OfferSchemaApiRoot | null,
    offer?: OfferResponse,
  ): void {
    this.schemaRoot.set(root);
    this.applySchemaToFormFields();
    this.initEmptySchemaArrays();

    if (!offer) {
      this.isHydratingForm = false;
      return;
    }
    const mapped = this.mapOfferToFormData(offer);
    this.initialData.set(mapped);
    this.applyExclusionEffects(mapped);
    this.schemaArraysDirty = false;
    this.isHydratingForm = false;
    this.loading.set(false);
  }

  private normalizeOfferKind(
    raw: unknown,
    fallback = 'PERIODIC_DISCOUNT',
  ): string {
    const s = raw != null ? String(raw).trim() : '';
    return s || fallback;
  }

  private cloneBaseFields(): FormField[] {
    return withPaginatedFormFields(
      OFFER_BASE_FORM_FIELDS.map((f) => ({
        ...f,
        options: f.options ? [...f.options] : undefined,
      })),
      {
        priceGroupId: {
          loadPage: ({ page, size, searchText }) => {
            const query = searchText.trim();
            return this.priceGroupController
              .list1(
                undefined,
                query ? [query] : undefined,
                undefined,
                undefined,
                'body',
                false,
                { httpHeaderAccept: 'application/json' as any },
              )
              .pipe(
                map((res) => {
                  const items = res ?? [];
                  const filtered = query
                    ? items.filter((x) => {
                        const code = (x.code ?? '').toLowerCase();
                        const desc = (x.description ?? '').toLowerCase();
                        const q = query.toLowerCase();
                        return code.includes(q) || desc.includes(q);
                      })
                    : items;
                  const start = (page - 1) * size;
                  const pageContent = filtered.slice(start, start + size);
                  return mapPageContentToPaginatedPage(
                    {
                      pageContent,
                      totalElements: filtered.length,
                    },
                    (x: PriceGroup) => ({
                      label: `${x.code ?? ''}${x.description ? ' - ' + x.description : ''}`,
                      value: x.id ?? 0,
                    }),
                  );
                }),
                catchError(() => of({ options: [], pageContentLength: 0 })),
              );
          },
        },
        validationPeriodId: {
          loadPage: ({ page, size, searchText }) => {
            const query = searchText.trim();
            return this.validationPeriodController
              .list(
                undefined,
                query ? [query] : undefined,
                undefined,
                'body',
                false,
                { httpHeaderAccept: 'application/json' as any },
              )
              .pipe(
                map((res) => {
                  const items = res ?? [];
                  const filtered = query
                    ? items.filter((x) => {
                        const code = (x.code ?? '').toLowerCase();
                        const desc = (x.description ?? '').toLowerCase();
                        const q = query.toLowerCase();
                        return code.includes(q) || desc.includes(q);
                      })
                    : items;
                  const start = (page - 1) * size;
                  const pageContent = filtered.slice(start, start + size);
                  return mapPageContentToPaginatedPage(
                    {
                      pageContent,
                      totalElements: filtered.length,
                    },
                    (x: ValidationPeriodListResponse) => ({
                      label: `${x.code ?? ''}${x.description ? ' - ' + x.description : ''}`,
                      value: x.id ?? 0,
                    }),
                  );
                }),
                catchError(() => of({ options: [], pageContentLength: 0 })),
              );
          },
        },
      },
    );
  }

  private applySchemaToFormFields(): void {
    const dyn = (this.schemaRoot()?.fields ?? [])
      .map((f) => mapOfferSchemaFieldToFormField(f))
      .filter((x): x is FormField => x !== null);
    this.formFields.set([...this.cloneBaseFields(), ...dyn]);
  }

  /**
   * Mutually-exclusive config, driven ENTIRELY by the backend schema (no hardcoded rules).
   * Each schema field may carry {@code disabledWhen} conditions; when any holds, the field is
   * ignored by the evaluator, so the UI disables + annotates it. Supported ops: FILLED, NON_EMPTY,
   * EQUALS, NOT_EQUALS — evaluated against sibling fields in the same definition.
   * Returns controlName -> note (VN preferred) for fields that must be disabled.
   */
  private computeExclusion(formData: Record<string, unknown>): Map<string, string> {
    const out = new Map<string, string>();
    const isFilled = (name: string): boolean => {
      const v = formData[definitionControlName(name)];
      if (v === null || v === undefined || v === '') return false;
      const n = Number(v);
      return Number.isFinite(n) ? n > 0 : String(v).trim() !== '';
    };
    const arrNonEmpty = (name: string): boolean =>
      (this.schemaArrayData()[name]?.length ?? 0) > 0;
    const strVal = (name: string): string => {
      const v = formData[definitionControlName(name)];
      return v === null || v === undefined ? '' : String(v);
    };

    const vi = (this.translateService.currentLang || 'vi').startsWith('vi');
    for (const f of this.schemaRoot()?.fields ?? []) {
      const conds = (f as { disabledWhen?: OfferFieldCondition[] }).disabledWhen;
      if (!f.name || !conds?.length) continue;
      for (const c of conds) {
        let hit = false;
        switch (c.op) {
          case 'FILLED':
            hit = isFilled(c.field);
            break;
          case 'NON_EMPTY':
            hit = arrNonEmpty(c.field);
            break;
          case 'EQUALS':
            hit = strVal(c.field) === String(c.value ?? '');
            break;
          case 'NOT_EQUALS':
            hit = strVal(c.field) !== String(c.value ?? '');
            break;
        }
        if (hit) {
          out.set(
            definitionControlName(f.name),
            (vi ? c.labelVi : c.label) || c.labelVi || c.label || '',
          );
          break;
        }
      }
    }
    return out;
  }

  private applyExclusionEffects(formData: Record<string, unknown>): void {
    const disabled = this.computeExclusion(formData);
    let changed = false;
    const next = this.formFields().map((f) => {
      if (!f.controlName.startsWith(DEF_FIELD_PREFIX)) return f;
      const note = disabled.get(f.controlName);
      const shouldDisable = note !== undefined;
      if (!!f.disabled === shouldDisable && (f.hintVi ?? undefined) === note) {
        return f;
      }
      changed = true;
      return { ...f, disabled: shouldDisable, hint: note, hintVi: note };
    });
    if (changed) {
      this.formFields.set(next);
    }
  }

  private recomputeExclusion(): void {
    this.applyExclusionEffects(this.addFormComponent?.form?.getRawValue() ?? {});
  }

  private initEmptySchemaArrays(): void {
    const defs = listSchemaArrayFields(this.schemaRoot()?.fields);
    const next: Record<string, SchemaArrayRow[]> = {};
    for (const d of defs) {
      next[d.name] = [];
    }
    this.schemaArrayData.set(next);
  }

  private setDefaultOfferFormValues(): void {
    this.initialData.set({
      code: '',
      offerType: null,
      offerName: '',
      description: '',
      status: 'DRAFT',
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
      priority: 0,
      stackable: true,
      salesChannelCodes: '',
    });
    this.currentOfferType.set('');
    this.schemaRoot.set(null);
    this.schemaArrayData.set({});
    this.schemaArraysDirty = false;
    this.applySchemaToFormFields();
  }

  private saveOffer(formData: Record<string, unknown>): void {
    this.loading.set(true);
    const payload = this.buildOfferPayload(formData);
    const request$ = this.offerId
      ? this.offerService.updateOffer(this.offerId, payload)
      : this.offerService.createOffer(payload as OfferCreateRequest);
    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.onSaveSuccess(),
      error: (err: unknown) => this.onSaveError(err),
    });
  }

  private onSaveSuccess(): void {
    this.loading.set(false);
    this.skipCanDeactivate = true;
    this.schemaArraysDirty = false;
    this.addFormComponent?.form?.markAsPristine();
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant(
        this.offerId
          ? 'promotion_management.messages.update_success'
          : 'promotion_management.messages.create_success',
      ),
    });
    this.router.navigate(['/marketing/promotion']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false) && !this.schemaArraysDirty;
  }

  private onSaveError(err?: unknown): void {
    this.loading.set(false);
    const errorBody = (err as { error?: { message?: string; errorCode?: string } } | undefined)?.error;
    const errorCode = errorBody?.errorCode;
    const translationKey = errorCode
      ? `promotion_management.messages.errors.${errorCode}`
      : null;
    const translatedMsg = translationKey
      ? this.translateService.instant(translationKey)
      : null;
    const detail =
      (translatedMsg && translatedMsg !== translationKey)
        ? translatedMsg
        : this.translateService.instant(
            this.offerId
              ? 'promotion_management.messages.update_error'
              : 'promotion_management.messages.create_error',
          );
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail,
      life: 8000,
    });
  }

  private buildOfferPayload(
    formData: Record<string, unknown>,
  ): OfferUpdateRequest {
    const offerType = this.normalizeOfferKind(formData['offerType'], '');
    const statusRaw =
      (formData['status'] as string | undefined)
      ?? OfferUpdateRequest.StatusEnum.Draft;
    return {
      code: (formData['code'] as string | undefined) ?? '',
      displayName: (formData['offerName'] as string | undefined) ?? '',
      shortDescription: (formData['description'] as string | undefined) ?? '',
      kind: (offerType || undefined) as OfferUpdateRequest.KindEnum | undefined,
      status: statusRaw as OfferUpdateRequest.StatusEnum,
      priority: (formData['priority'] as number | undefined) ?? undefined,
      stackable: (formData['stackable'] as boolean | undefined) ?? true,
      startDate: formatApiDate(formData['startDate']),
      endDate: formatApiDate(formData['endDate']),
      startTime: formatApiLocalTime(formData['startTime']) as OfferUpdateRequest['startTime'],
      endTime: formatApiLocalTime(formData['endTime']) as OfferUpdateRequest['endTime'],
      salesChannelCodes:
        (formData['salesChannelCodes'] as string | undefined) ?? undefined,
      definitionJson: this.buildDefinitionJson(formData),
    };
  }

  private mapOfferToFormData(offer: OfferResponse): Record<string, unknown> {
    const parsed = this.safeParseDefinitionJson(offer.definitionJson) as
      | Record<string, unknown>
      | null;
    const fields = this.schemaRoot()?.fields ?? [];
    const flat = flattenDefinitionForForm(parsed, fields);
    this.hydrateSchemaArrays(parsed);
    return {
      code: offer.code ?? '',
      offerType: this.normalizeOfferKind(offer.kind ?? 'PERIODIC_DISCOUNT'),
      offerName: offer.displayName ?? '',
      description: offer.shortDescription ?? '',
      status: this.normalizeStatusForForm(offer.status),
      startDate: dateFromApiDate(offer.startDate),
      endDate: dateFromApiDate(offer.endDate),
      startTime: dateFromLocalTime(offer.startTime),
      endTime: dateFromLocalTime(offer.endTime),
      priority: offer.priority ?? 0,
      stackable: offer.stackable ?? true,
      salesChannelCodes: offer.salesChannelCodes ?? '',
      ...flat,
    };
  }

  private normalizeStatusForForm(status?: string): string {
    if (!status) return 'DRAFT';
    if (status === 'ENABLED') return 'ACTIVE';
    if (status === 'DISABLED' || status === 'ENDED') return 'INACTIVE';
    return status;
  }

  private hydrateSchemaArrays(parsed: Record<string, unknown> | null): void {
    const defs = listSchemaArrayFields(this.schemaRoot()?.fields);
    const next: Record<string, SchemaArrayRow[]> = {};
    for (const d of defs) {
      const raw = this.getRawArray(parsed, d.name);
      next[d.name] = rowsFromApiArray(raw, d.itemProperties);
    }
    this.schemaArrayData.set(next);
  }

  private getRawArray(
    parsed: Record<string, unknown> | null,
    fieldName: string,
  ): unknown {
    if (!parsed) {
      return undefined;
    }
    return getNested(parsed, fieldName.split('.'));
  }

  private buildDefinitionJson(
    formData: Record<string, unknown>,
  ): string | undefined {
    const fields = this.schemaRoot()?.fields;
    if (!fields?.length) {
      return '{}';
    }
    const root: Record<string, unknown> = {};
    const arrayDefs = listSchemaArrayFields(fields);
    for (const d of arrayDefs) {
      const rows = this.schemaArrayData()[d.name] ?? [];
      setNested(root, d.name.split('.'), serializeRowsForApi(rows));
    }
    for (const f of fields) {
      if (jsonTypeOf(f) === 'ARRAY' || !f.name) {
        continue;
      }
      const enc = definitionControlName(f.name);
      setNested(root, f.name.split('.'), formData[enc]);
    }
    return JSON.stringify(root);
  }

  private safeParseDefinitionJson(definitionJson?: string): unknown {
    if (!definitionJson) {
      return null;
    }
    try {
      return JSON.parse(definitionJson);
    } catch {
      return null;
    }
  }
}
